import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Upload, Trash2, SlidersHorizontal, Circle, Lock, Unlock, Wand2, Plug, Mic2, MoveHorizontal, Maximize2, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { METER_BAR_COUNT, WAVE_BIN_COUNT, EFFECT_LIST, STRIP_HOVER, clamp, padCh, tintFor, trimName } from "./dawHelpers";
import PluginManagerPanel from "@/components/PluginManagerPanel";

/* ─────────────────────────────  Sub-pieces  ───────────────────────────── */
function ChannelMeter({ peak }) {
  const safePeak = peak || 0;
  return (
    <div className="mb-3 h-24 rounded-xl border border-white/10 bg-black/40 p-1.5">
      <div className="flex h-full items-end gap-[3px]">
        {Array.from({ length: METER_BAR_COUNT }).map((_, i) => {
          const lit = i < safePeak * METER_BAR_COUNT;
          const heightPct = clamp((safePeak || 0.02) * 100 - i * 6 + 14, 6, 100);
          const color = i > 11 ? "bg-rose-400" : i > 8 ? "bg-amber-300" : "bg-emerald-300";
          return (
            <div
              key={`meter-bar-${i}`}
              className={`flex-1 rounded-sm transition-all duration-75 ${lit ? color : "bg-white/10"}`}
              style={{ height: `${heightPct}%`, opacity: lit ? 1 : 0.35 }}
            />
          );
        })}
      </div>
    </div>
  );
}

function MiniWave({ channel, tintDot, liveSamples = null, isRecording = false, clipping = false }) {
  const waveform = channel?.waveform;
  return (
    <div className={`relative mb-3 h-8 rounded-md border px-1.5 transition-colors ${clipping ? "border-red-400/70 bg-red-500/15" : "border-white/10 bg-black/30"}`}>
      <div className="flex h-full items-center gap-[1.5px]">
        {(waveform || Array.from({ length: WAVE_BIN_COUNT }, () => 0.1)).map((v, i) => (
          <span
            key={`mini-wave-bin-${i}`}
            className={`w-[2px] rounded-full ${channel?.buffer ? tintDot : "bg-white/15"}`}
            style={{ height: `${Math.max(6, v * 100)}%` }}
          />
        ))}
      </div>
      {/* iter-199 — Live wave overlay on top of the static mini-wave so the
          producer instantly sees which channel is firing in a 24-strip
          mixer view. Cyan = playback, rose = active record channel.
          iter-200 — bright red + extra glow when clipping (≥ 0.95 for 2+
          consecutive samples within the last 200 ms). */}
      {liveSamples && liveSamples.length > 0 && (
        <div
          data-testid="channel-strip-live-wave"
          className="pointer-events-none absolute inset-0 flex items-center gap-[1.5px] px-1.5"
        >
          {liveSamples.slice(-WAVE_BIN_COUNT).map((v, i) => (
            <span
              key={`cs-lw-${i}`}
              className={`w-[2px] rounded-full ${
                clipping
                  ? "bg-red-300 shadow-[0_0_3px_rgba(252,165,165,0.95)]"
                  : isRecording
                    ? "bg-rose-200 shadow-[0_0_2px_rgba(254,205,211,0.7)]"
                    : "bg-cyan-200 shadow-[0_0_2px_rgba(165,243,252,0.7)]"
              }`}
              style={{ height: `${Math.max(6, v * 100)}%` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MuteSoloButtons({ index, muted, solo, armed, onMute, onSolo, onToggleArm, isRecording, onRecordChannel }) {
  return (
    <div className="mt-3 grid grid-cols-4 gap-1.5">
      <button
        data-testid={`channel-mute-${index}`}
        onClick={onMute}
        className={`rounded-lg px-2 py-1 font-mono text-[11px] font-bold transition ${
          muted ? "bg-rose-500 text-white shadow-[0_0_12px_rgba(244,63,94,0.4)]" : "bg-white/10 text-white/70 hover:bg-white/15"
        }`}
      >M</button>
      <button
        data-testid={`channel-solo-${index}`}
        onClick={onSolo}
        className={`rounded-lg px-2 py-1 font-mono text-[11px] font-bold transition ${
          solo ? "bg-amber-300 text-black shadow-[0_0_12px_rgba(252,211,77,0.4)]" : "bg-white/10 text-white/70 hover:bg-white/15"
        }`}
      >S</button>
      <button
        data-testid={`channel-arm-${index}`}
        onClick={onToggleArm}
        title={armed ? "Disarm" : "Arm for multi-track recording"}
        className={`grid place-items-center rounded-lg px-2 py-1 font-mono text-[11px] font-bold transition ${
          armed ? "bg-rose-500/60 text-white shadow-[0_0_12px_rgba(244,63,94,0.55)] animate-pulse" : "bg-white/10 text-white/70 hover:bg-rose-500/20 hover:text-rose-200"
        }`}
      >
        <Circle className={`h-3 w-3 ${armed ? "fill-current" : ""}`} />
      </button>
      {/* Per-strip one-click record — bypasses arm flow, starts/stops recording
          on THIS channel directly. Distinct emerald accent + mic icon so the
          user instantly knows what it does without reading a tooltip. */}
      <button
        data-testid={`channel-rec-${index}`}
        onClick={(e) => { e.stopPropagation(); onRecordChannel?.(index); }}
        title={isRecording ? "Stop recording" : "Record this channel (one click)"}
        disabled={!onRecordChannel}
        className={`grid place-items-center rounded-lg px-2 py-1 font-mono text-[11px] font-bold transition disabled:opacity-40 ${
          isRecording
            ? "bg-rose-500 text-white shadow-[0_0_12px_rgba(244,63,94,0.6)] animate-pulse"
            : "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/40 hover:bg-emerald-500/40 hover:text-white"
        }`}
      >
        <Mic2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function LoadClearButtons({ index, onLoadClick, onClear, disabled }) {
  return (
    <div className="mt-2 grid grid-cols-2 gap-1.5">
      <button
        data-testid={`channel-upload-${index}`}
        onClick={onLoadClick}
        className="flex items-center justify-center gap-1 rounded-lg bg-white/[0.06] px-2 py-1 text-[10px] text-white/75 hover:bg-white/10"
      >
        <Upload className="h-3 w-3" /> Load
      </button>
      <button
        data-testid={`channel-clear-${index}`}
        onClick={onClear}
        disabled={disabled}
        className="flex items-center justify-center gap-1 rounded-lg bg-white/[0.06] px-2 py-1 text-[10px] text-white/75 hover:bg-white/10 disabled:opacity-40"
      >
        <Trash2 className="h-3 w-3" /> Clear
      </button>
    </div>
  );
}

function FxPopover({ index, activeFx, fxCount, onToggle }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          data-testid={`channel-fx-btn-${index}`}
          title={fxCount > 0 ? `${fxCount} active effect${fxCount === 1 ? "" : "s"} — click to edit` : "Open per-channel FX rack"}
          className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 font-mono text-[9px] font-bold transition ${
            fxCount > 0
              ? "bg-fuchsia-500/40 text-fuchsia-100 ring-1 ring-fuchsia-300/40 hover:bg-fuchsia-500/55"
              : "bg-white/[0.06] text-white/55 hover:bg-white/[0.12] hover:text-white/85"
          }`}
        >
          <SlidersHorizontal className="h-2.5 w-2.5" />
          {fxCount > 0 ? `FX·${fxCount}` : "FX"}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="right"
        className="w-64 border-white/10 bg-[#0c0f1e] p-3 text-white"
        data-testid={`channel-fx-popover-${index}`}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-fuchsia-300">
          FX · CH {padCh(index)}
        </p>
        <div className="space-y-1">
          {EFFECT_LIST.map(({ key, label, hint }) => {
            const isOn = !!activeFx?.[key];
            return (
              <label
                key={`strip-fx-${index}-${key}`}
                className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg border px-2 py-1.5 transition ${
                  isOn ? "border-fuchsia-400/50 bg-fuchsia-500/10" : "border-white/10 bg-black/30 hover:bg-black/40"
                }`}
              >
                <span className="flex flex-col">
                  <span className={`text-[12px] ${isOn ? "text-white" : "text-white/85"}`}>{label}</span>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-white/40">{hint}</span>
                </span>
                <input
                  data-testid={`strip-fx-${index}-${key}`}
                  type="checkbox"
                  checked={isOn}
                  onChange={() => onToggle(key)}
                  className="h-3.5 w-3.5 accent-fuchsia-400"
                />
              </label>
            );
          })}
        </div>
        <p className="mt-2 font-mono text-[9px] uppercase tracking-wider text-white/35">
          Live Web Audio · per-channel
        </p>
      </PopoverContent>
    </Popover>
  );
}

function ChannelHeader({ index, channel, tintGrad, fxCount, activeFx, onToggleFx, clippedRecently = false }) {
  return (
    <div className={`mb-3 rounded-xl bg-gradient-to-br ${tintGrad} px-2.5 py-2`}>
      <div className="mb-1 flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">CH {padCh(index)}</p>
        <div className="flex items-center gap-1">
          {/* iter-201 — lingering CLIPPED badge in the strip header. */}
          {clippedRecently && (
            <span
              data-testid={`channel-strip-clipped-${index}`}
              title="This channel clipped recently — bring the fader down."
              className="rounded-full bg-red-500/95 px-1.5 py-[1px] font-mono text-[8.5px] font-bold uppercase tracking-wider text-white shadow-[0_0_6px_rgba(239,68,68,0.9)] animate-pulse"
            >Clip</span>
          )}
          <FxPopover index={index} activeFx={activeFx} fxCount={fxCount} onToggle={(key) => onToggleFx?.(index, key)} />
        </div>
      </div>
      <p
        className="truncate font-display text-[13px] font-semibold leading-tight text-white"
        title={channel?.name || `Track ${index + 1}`}
      >
        {trimName(channel?.name) || `Track ${index + 1}`}
      </p>
    </div>
  );
}

/* ─────────────────────────────  Channel Strip  ───────────────────────────── */
export default function ChannelStrip({ index, selected, onSelect, channel, engine, peak, externalVolume, onUpload, onClear, recordingIdx, fxCount, activeFx, onToggleFx, armed, onToggleArm, onRecordChannel,
  // Phase-3 channel-lock props (same shape as MixerRow).
  lockedByMe = false, lockedByOther = false, lockHolder = null, onToggleLock = null,
  // iter-199 — live waveform samples for this strip's MiniWave overlay.
  liveSamples = null, clipping = false, clippedRecently = false, liveWaveRev = 0,
  // iter-265 — loop-region length (seconds). Drives the "Fit to loop" affordance
  // on the time-stretch row. When undefined / 0, the Fit button is disabled.
  loopLengthSec = 0,
}) {
  void liveWaveRev; // present only so parent re-renders trigger this child
  const tint = tintFor(index);
  const [vol, setVol] = useState(72);
  const [pan, setPan] = useState(0);
  // Pitch bend (semitones, -12..+12). Echoes engine state. Persists across
  // channel-buffer reloads (the engine's Channel object owns the value).
  const [pitchBend, setPitchBend] = useState(channel?.pitchBendSemi ?? 0);
  const [muted, setMuted] = useState(false);
  const [solo, setSolo] = useState(false);
  // Plugin manager modal state. Local-only — driven by the per-strip "PLG" button.
  const [pluginOpen, setPluginOpen] = useState(false);
  const pluginCount = channel?.plugins?.length || 0;
  // iter-265 — Time-stretch state. Slider value is a 0.5×–2.0× factor;
  // 1.0 = pristine (no stretch). The engine owns the *authoritative*
  // factor on `channel.stretchFactor`; we mirror it locally for the
  // slider control. `stretching` shows a tiny spinner during the SOLA
  // render so the user knows we heard the slider.
  const [stretchFactor, setStretchFactor] = useState(channel?.stretchFactor ?? 1.0);
  const [stretching, setStretching] = useState(false);
  const stretchDebounceRef = useRef(null);
  const cStretchFactor = channel?.stretchFactor;
  // Flash key bumps on every EXTERNAL volume change (MIDI / preset / etc.) — used
  // to re-key the framer-motion wrapper so the colour-pulse animation restarts.
  const [flashKey, setFlashKey] = useState(0);
  // Last value the user set via the slider, so we can distinguish "MIDI moved me"
  // vs the engine echo from our own setChannelVolume call.
  const lastUserSetRef = useRef(externalVolume ?? channel?.volume ?? 72);
  const fileRef = useRef(null);

  const cPan = channel?.pan;
  const cMuted = channel?.muted;
  const cSolo = channel?.solo;
  const cPitchBend = channel?.pitchBendSemi;

  useEffect(() => {
    if (channel) {
      setPan(cPan);
      setMuted(cMuted);
      setSolo(cSolo);
      if (typeof cPitchBend === "number") setPitchBend(cPitchBend);
      // iter-265 — Echo back the engine's stretchFactor so the slider
      // shows the right value after a stretch finishes / after a load.
      if (typeof cStretchFactor === "number") setStretchFactor(cStretchFactor);
      else setStretchFactor(1.0);
    }
  }, [channel, cPan, cMuted, cSolo, cPitchBend, cStretchFactor]);

  // Volume sync — when the engine reports a different value than what the user
  // last set locally, it's an external source (MIDI, preset, voice TTS) and we
  // trigger the dramatic colour-pulse animation on top of mirroring the value.
  useEffect(() => {
    if (externalVolume == null) return;
    const lastUser = lastUserSetRef.current;
    if (externalVolume !== lastUser) {
      setVol(externalVolume);
      setFlashKey((k) => k + 1);
      // Adopt the new value as the "settled" point — prevents a re-flash on the
      // next render if the engine keeps echoing the same number.
      lastUserSetRef.current = externalVolume;
    } else if (externalVolume !== vol) {
      // Echo from our own setChannelVolume — just sync, no flash.
      setVol(externalVolume);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalVolume]);

  const isRecording = recordingIdx === index;

  const onVolChange = (e) => {
    const v = Number(e.target.value);
    setVol(v);
    lastUserSetRef.current = v;
    engine.setChannelVolume(index, v);
  };
  const onPanChange = (e) => {
    const v = Number(e.target.value); setPan(v); engine.setChannelPan(index, v);
  };
  const onPitchBendChange = (e) => {
    const v = Number(e.target.value);
    setPitchBend(v);
    engine.setChannelPitchBend(index, v);
  };
  const onPitchBendReset = (e) => {
    e?.stopPropagation();
    setPitchBend(0);
    engine.setChannelPitchBend(index, 0);
  };
  // iter-265 — Pitch-preserving time-stretch handlers. The slider drag
  // is debounced (400 ms) because each change kicks an OfflineAudio-
  // Context render via SOLA which is ~50-200 ms of CPU. Without debounce
  // a single drag would queue 30+ renders. With debounce we get exactly
  // ONE render per "settled" slider value.
  const _runStretch = async (factor) => {
    if (!engine || !channel?.buffer) return;
    setStretching(true);
    try {
      await engine.stretchChannel(index, factor);
    } catch (e) {
      void e;
      toast.error("Time-stretch failed", { description: "Try a less extreme factor (0.5×–2×)." });
    } finally {
      setStretching(false);
    }
  };
  const onStretchChange = (e) => {
    const v = Number(e.target.value) / 100; // slider range is 50-200 (integer) → 0.5-2.0
    setStretchFactor(v);
    // iter-265b — Scrub preview: instantly retune the live source's
    // playbackRate so the producer HEARS the new length while dragging
    // (with pitch shift — the SOLA render that follows in 250 ms lands
    // a pitch-preserved version). Without this, the slider felt dead
    // until the debounce fired.
    if (engine?.setChannelPreviewStretchRate) {
      try { engine.setChannelPreviewStretchRate(index, v); } catch (e2) { void e2; }
    }
    if (stretchDebounceRef.current) clearTimeout(stretchDebounceRef.current);
    stretchDebounceRef.current = setTimeout(() => {
      stretchDebounceRef.current = null;
      void _runStretch(v);
    }, 250);
  };
  const onStretchReset = (e) => {
    e?.stopPropagation();
    if (stretchDebounceRef.current) {
      clearTimeout(stretchDebounceRef.current);
      stretchDebounceRef.current = null;
    }
    setStretchFactor(1.0);
    void _runStretch(1.0);
  };
  const onFitToLoop = async (e) => {
    e?.stopPropagation();
    if (!engine?.stretchChannelToLoop || !channel?.buffer) return;
    if (!(loopLengthSec > 0)) {
      toast.message("Set a loop range first", {
        description: "Drag the A↔B markers above the timeline to define a loop, then tap Fit again.",
      });
      return;
    }
    setStretching(true);
    try {
      const result = await engine.stretchChannelToLoop(index);
      if (result?.factor != null) {
        setStretchFactor(result.factor);
        toast.success("Stretched to loop length", {
          description: `${result.originalDuration.toFixed(2)}s → ${result.newDuration.toFixed(2)}s (×${result.factor.toFixed(2)}, pitch preserved).`,
        });
      }
    } catch (e2) {
      void e2;
      toast.error("Fit-to-loop failed");
    } finally {
      setStretching(false);
    }
  };
  // Clear pending stretch on unmount so we don't fire after the strip
  // is gone.
  useEffect(() => () => {
    if (stretchDebounceRef.current) clearTimeout(stretchDebounceRef.current);
  }, []);
  const onMuteClick = (e) => {
    e.stopPropagation();
    const next = !muted; setMuted(next); engine.setChannelMute(index, next);
  };
  const onSoloClick = (e) => {
    e.stopPropagation();
    const next = !solo; setSolo(next); engine.setChannelSolo(index, next);
  };
  const onLoadClick = (e) => { e.stopPropagation(); fileRef.current?.click(); };
  const onClearClick = (e) => { e.stopPropagation(); onClear(index); };
  const onFilePicked = (e) => {
    const f = e.target.files?.[0]; if (f) onUpload(index, f); e.target.value = "";
  };
  const stopProp = (e) => e.stopPropagation();

  // Phase-3 write-gate: when another peer holds this channel's lock, block
  // local mutations + flash a toast. CRDT echoes from the holder still
  // apply via the engine bridge — this only prevents US from racing them.
  const checkLock = () => {
    if (lockedByOther) {
      toast.info("Channel locked", {
        description: `${lockHolder?.name || "Someone"} has exclusive control of CH ${padCh(index)}.`,
        duration: 2200,
      });
      return false;
    }
    return true;
  };

  // Gated versions of the four mutation paths. Defined AFTER the raw handlers
  // so they shadow them by name in the JSX below.
  const onVolChangeGated  = (e) => { if (checkLock()) onVolChange(e); };
  const onPanChangeGated  = (e) => { if (checkLock()) onPanChange(e); };
  const onMuteClickGated  = (e) => { if (checkLock()) onMuteClick(e); };
  const onSoloClickGated  = (e) => { if (checkLock()) onSoloClick(e); };
  const onPitchBendChangeGated = (e) => { if (checkLock()) onPitchBendChange(e); };
  // iter-265 — Lock-aware stretch handlers. While a lock is held by
  // another user, the local user can still SEE the slider position but
  // any change is dropped so the channel state stays in sync.
  const onStretchChangeGated = (e) => { if (checkLock()) onStretchChange(e); };
  const onFitToLoopGated = (e) => { if (checkLock()) onFitToLoop(e); };

  return (
    <motion.div
      layout
      onClick={() => onSelect(index)}
      whileHover={STRIP_HOVER}
      data-testid={`channel-strip-${index}`}
      className={`relative min-w-[128px] cursor-pointer rounded-2xl border p-3 backdrop-blur-sm transition ${
        selected ? `border-white/40 bg-white/10 ring-2 ${tint.ring}` : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]"
      }`}
    >
      {isRecording && (
        <span className="absolute -right-1 -top-1 flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-500" />
        </span>
      )}

      {/* iter-77 — lineage-group color stripe. When this channel is part
          of a mute/solo-linked group (set by SongLibrary.handleBounceLineage
          when bouncing an ELPM derivation lineage), render a 3px-wide
          vertical stripe on the left edge in the group's color so the
          user can visually identify which channels move together. */}
      {channel?.groupColor ? (
        <span
          data-testid={`channel-strip-group-${index}`}
          aria-hidden
          className="pointer-events-none absolute inset-y-2 left-0 w-[3px] rounded-r"
          style={{ backgroundColor: channel.groupColor, boxShadow: `0 0 8px ${channel.groupColor}80` }}
          title={channel.groupId ? `Linked group: ${channel.groupId}` : "Linked group"}
        />
      ) : null}

      <ChannelHeader index={index} channel={channel} tintGrad={tint.grad} fxCount={fxCount} activeFx={activeFx} onToggleFx={onToggleFx} clippedRecently={clippedRecently} />

      {/* Phase-3 channel lock badge — top-right corner overlay. Only renders
          when the parent is wired to collab (onToggleLock != null). Click
          acquires (if unlocked) / releases (if mine). Click is a no-op when
          another peer holds it — the badge alone tells the user who's in
          control. */}
      {onToggleLock ? (
        <button
          type="button"
          data-testid={`channel-strip-lock-${index}`}
          aria-pressed={lockedByMe || lockedByOther}
          title={
            lockedByMe ? "You hold this channel — click to release"
            : lockedByOther ? `Locked by ${lockHolder?.name || "Someone"}`
            : "Acquire exclusive control"
          }
          onClick={(e) => {
            e.stopPropagation();
            if (lockedByOther) return;
            onToggleLock();
          }}
          className={`absolute right-2 top-2 z-10 grid h-6 w-6 place-items-center rounded-md transition ${
            lockedByMe
              ? "bg-emerald-500/30 text-emerald-200 ring-1 ring-emerald-400/60 shadow-[0_0_10px_rgba(52,211,153,0.45)]"
              : lockedByOther
              ? "ring-1 ring-white/15 text-white"
              : "bg-white/[0.04] text-white/35 hover:bg-white/[0.1] hover:text-white/70"
          }`}
          style={lockedByOther && lockHolder?.color ? { background: lockHolder.color + "55" } : undefined}
        >
          {lockedByMe || lockedByOther ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
        </button>
      ) : null}

      <ChannelMeter peak={peak} />
      <MiniWave channel={channel} tintDot={tint.dot} liveSamples={liveSamples} isRecording={isRecording} clipping={clipping} />

      <motion.div
        key={`vol-flash-${flashKey}`}
        initial={flashKey === 0 ? false : {
          boxShadow: `0 0 0px ${tint.glow}`,
          backgroundColor: "rgba(255,255,255,0)",
          scale: 0.97,
        }}
        animate={{
          boxShadow: [
            `0 0 0px ${tint.glow}`,
            `0 0 24px ${tint.glow}`,
            `0 0 12px ${tint.glow}`,
            `0 0 0px ${tint.glow}`,
          ],
          backgroundColor: [
            "rgba(255,255,255,0)",
            tint.glow,
            "rgba(255,255,255,0.06)",
            "rgba(255,255,255,0)",
          ],
          scale: [0.97, 1.04, 1.01, 1],
        }}
        transition={{ duration: 0.8, times: [0, 0.18, 0.4, 1], ease: "easeOut" }}
        className="rounded-lg px-1"
      >
        <label
          className="mb-1 block font-mono text-[10px] uppercase tracking-wider transition-colors duration-300"
          style={flashKey > 0 ? { color: tint.accent } : { color: "rgba(255,255,255,0.45)" }}
        >
          Vol · {vol}
        </label>
        <input
          data-testid={`channel-volume-${index}`}
          type="range" min="0" max="100" value={vol}
          onClick={stopProp}
          onChange={onVolChangeGated}
          className="studio-fader w-full"
          style={{ accentColor: tint.accent }}
        />
      </motion.div>

      <label className="mb-1 mt-2 block font-mono text-[10px] uppercase tracking-wider text-white/45">
        Pan · {pan === 0 ? "C" : pan < 0 ? `L${Math.abs(pan)}` : `R${pan}`}
      </label>
      <input
        data-testid={`channel-pan-${index}`}
        type="range" min="-50" max="50" value={pan}
        onClick={stopProp}
        onChange={onPanChangeGated}
        className="w-full accent-white"
      />

      {/* Pitch Bend — real-time semitone offset (-12 .. +12) applied to every
          active audio source's detune param. Double-click the label to snap
          back to 0 ("center"). */}
      <div className="mt-2 flex items-center justify-between">
        <button
          type="button"
          data-testid={`channel-pitch-reset-${index}`}
          onDoubleClick={onPitchBendReset}
          onClick={(e) => { e.stopPropagation(); /* single click no-op */ }}
          title="Double-click to reset pitch bend to 0"
          className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-white/45 hover:text-white/70"
        >
          <Wand2 className="h-2.5 w-2.5" />
          Pitch · {pitchBend > 0 ? `+${pitchBend}` : pitchBend}st
        </button>
      </div>
      <input
        data-testid={`channel-pitch-${index}`}
        type="range" min="-12" max="12" step="1" value={pitchBend}
        onClick={stopProp}
        onChange={onPitchBendChangeGated}
        className="w-full accent-fuchsia-400"
      />

      {/* iter-265 — Pitch-preserving time-stretch row. Drag to change
          the channel's length (0.5× to 2.0×) WITHOUT shifting pitch.
          Double-click the label to snap back to 1.0×. The "Fit" button
          stretches to exactly match the current loop length (great for
          warping a sample to a beat). The whole row is hidden when the
          channel has no audio loaded — keeps the strip compact for
          empty channels. */}
      {channel?.buffer && (
        <>
          <div className="mt-2 flex items-center justify-between">
            <button
              type="button"
              data-testid={`channel-stretch-reset-${index}`}
              onDoubleClick={onStretchReset}
              onClick={(e) => { e.stopPropagation(); }}
              title="Double-click to reset stretch to 1.0× (pristine length)"
              className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-white/45 hover:text-white/70"
            >
              {stretching ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                <MoveHorizontal className="h-2.5 w-2.5" />
              )}
              Stretch · {stretchFactor.toFixed(2)}×
            </button>
            <button
              type="button"
              data-testid={`channel-stretch-fit-${index}`}
              onClick={onFitToLoopGated}
              disabled={!(loopLengthSec > 0) || stretching}
              title={loopLengthSec > 0
                ? `Fit to loop length (${loopLengthSec.toFixed(2)}s) · pitch preserved`
                : "Set a loop range first (drag A↔B above the timeline)"}
              className="flex items-center gap-0.5 rounded px-1 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/45 transition hover:bg-cyan-400/15 hover:text-cyan-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white/45"
            >
              <Maximize2 className="h-2.5 w-2.5" />
              Fit
            </button>
          </div>
          <input
            data-testid={`channel-stretch-${index}`}
            type="range" min="50" max="200" step="5" value={Math.round(stretchFactor * 100)}
            onClick={stopProp}
            onChange={onStretchChangeGated}
            disabled={stretching}
            className="w-full accent-cyan-400 disabled:opacity-50"
          />
        </>
      )}

      <MuteSoloButtons
        index={index}
        muted={muted}
        solo={solo}
        armed={armed}
        onMute={onMuteClickGated}
        onSolo={onSoloClickGated}
        onToggleArm={(e) => { e.stopPropagation(); onToggleArm?.(index); }}
        isRecording={isRecording}
        onRecordChannel={onRecordChannel}
      />
      <LoadClearButtons index={index} onLoadClick={onLoadClick} onClear={onClearClick} disabled={!channel?.buffer} />

      {/* Plugin host opener — opens a per-channel AudioWorklet plugin manager.
          Shows the active plugin count when any are loaded so the user can
          spot which channels carry custom processors at a glance. */}
      <button
        type="button"
        data-testid={`channel-plugin-btn-${index}`}
        onClick={(e) => { e.stopPropagation(); setPluginOpen(true); }}
        title={pluginCount > 0 ? `${pluginCount} plugin${pluginCount === 1 ? "" : "s"} loaded — click to manage` : "Add a synced AudioWorklet plugin"}
        className={`mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition ${
          pluginCount > 0
            ? "bg-cyan-500/[0.15] text-cyan-100 ring-1 ring-cyan-400/40 hover:bg-cyan-500/[0.25]"
            : "bg-white/[0.05] text-white/55 hover:bg-white/[0.12] hover:text-white/85"
        }`}
      >
        <Plug className="h-3 w-3" />
        {pluginCount > 0 ? `Plugins · ${pluginCount}` : "Plugins"}
      </button>

      <input
        ref={fileRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={onFilePicked}
      />

      <PluginManagerPanel
        open={pluginOpen}
        onClose={() => setPluginOpen(false)}
        channelIndex={index}
        channel={channel}
        engine={engine}
      />
    </motion.div>
  );
}
